import * as actionTypes from 'constants/action-types';
import _ from 'lodash';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import { Reducer } from 'redux';
import { isDirty, isMSStopped } from 'utils/mockServiceUtils';
import { MockServiceState } from 'store/mockServices/mockServiceTypes';
import { MockService, MockServiceOriginalValues } from 'types/rest/mockServiceTypes';
import { updateObjectInArray } from 'utils/miscUtil';

const initialState: MockServiceState = {
  dataList: [],
  msListCount: 0,
  mockServiceTemplateList: [],
  locationList: [],
  serviceList: [],
  serviceLogs: {
    apiVersion: 0,
    error: [],
    requestId: '',
    result: {
      httpRunnerLog: [],
      messagingRunnerLog: [],
    },
    isLoading: false,
    serviceMockId: 0,
    uniqueWindowId: 0,
    openWindowId: 0,
  },
  showUpgradeLoading: false,
  filterText: '',
  locData: [],
  showHostPortFlag: false,
  isLoading: false,
  expandedMockServiceId: null,
  activePage: 1,
  activeTab: 'main',
  activeParamTab: 'THINK_TIME',
  isUpgradeMockModalOpen: false,
  isMsInWorkspace: true,
  filter: { status: null, shipId: null, location: null, search: null },
};

export const mockServiceListReducer: Reducer<MockServiceState, MockServiceActionTypes> = (
  state = initialState,
  action,
): MockServiceState => {
  switch (action.type) {
    case actionTypes.VIEW_MOCK_SERVICES:
      const sortedMockServicesList = _.orderBy(action.payload, 'id', 'desc');

      const newState = { ...state };
      newState.loadTimeStamp = new Date().getTime();
      newState.dataList = _.map(sortedMockServicesList, item => {
        item = {
          ...item,
          templateId: 0,
          showHostPortFlag: item.liveSystemHost === 'bypassvalue',
          metaData: {
            isDeployed: item.status != null && item.status.toUpperCase() === 'RUNNING',
            isFailed: item.status != null && item.status.toUpperCase() === 'FAILED',
            inProgress: item.status != null && item.status.toUpperCase() === 'CONFIGURING',
            isStopped: isMSStopped(item),
            isNewest: false,
            isDirty: false,
            isFileAssetDirty: false,
            isInvalid: false,
          },
          original: {
            ...item,
            serviceMockId: item.serviceMockId,
          },
          hits: item.hits ? item.hits : 0,
        };
        return item;
      });
      newState.isLoading = false;
      newState.msListCount = newState.dataList.length;

      return newState;

    //TODO - create new action - mark as draft which will only mark item as draft, nothing else...
    case actionTypes.SWAP_DRAFT:
      const indexToOverride = _.findIndex(state.dataList, item => item.id === action.payload.idToReplace);
      let itemToOverride: MockService;

      // Merge attributes of the MS from payload to redux store
      itemToOverride = _.cloneDeep(state.dataList[indexToOverride]);

      // Copy the previous state to original, so we can use it for the diff
      itemToOverride.original = _.cloneDeep(state.dataList[indexToOverride]) as MockServiceOriginalValues;

      if (action.payload.mergeAllFields) {
        itemToOverride = _.merge(itemToOverride, action.payload.replacement);
      } else {
        itemToOverride.id = action.payload.replacement.id;
      }

      if (action.payload.isDraft === true) {
        itemToOverride.serviceMockId = action.payload.idToReplace;
      }

      itemToOverride.isDraft = action.payload.isDraft;
      itemToOverride.mockServiceTransactions = action.payload.replacement.mockServiceTransactions;

      //Replace item
      const updatedState = { ...state };
      updatedState.dataList = updateObjectInArray(updatedState.dataList, itemToOverride, indexToOverride);

      //If there is item open in the table, change its id
      if (updatedState.expandedMockServiceId !== null) {
        updatedState.expandedMockServiceId = itemToOverride.id;
      }

      return updatedState;

    case actionTypes.UPDATE_MS_HITS:
      let cloned = _.cloneDeep(state.dataList);

      _.forEach(action.payload, ms => {
        cloned = _.map(cloned, item => {
          if (
            (item.isDraft === true && item.serviceMockId !== null && item.serviceMockId === ms.serviceMockId) ||
            (item.isDraft === false && item.id === ms.serviceMockId)
          ) {
            item = {
              ...item,
              hits: ms.value,
            };
          }
          return item;
        });
      });
      return {
        ...state,
        dataList: cloned,
      };

    case actionTypes.LOADING_MOCK_SERVICES_LIST:
      return {
        ...state,
        isLoading: action.payload.displayLoading,
        activePage: 1,
      };
    case actionTypes.UPDATE_MOCK_SERVICES_LIST_COUNT:
      return {
        ...state,
        msListCount: action.payload.count,
      };
    case actionTypes.VIEW_LOCATION_LIST:
      return {
        ...state,
        locationList: action.payload,
      };
    case actionTypes.VIEW_SERVICE_LIST:
      return {
        ...state,
        serviceList: action.payload,
      };
    case actionTypes.VIEW_MOCK_SERVICE_TEMPLATES_LIST:
      return {
        ...state,
        mockServiceTemplateList: action.payload,
      };
    case actionTypes.MOCK_SERVICE_UPDATE_FILTER:
      return {
        ...state,
        filter: action.payload,
      };
    case actionTypes.DELETE_MOCK_SERVICE:
      return {
        ...state,
        dataList: _.filter(state.dataList, item => item.id !== action.payload.id),
      };
    case actionTypes.MOCK_SERVICE_CHANGED:
      const msChangedState = { ...state };
      msChangedState.dataList = _.map(msChangedState.dataList, item => {
        if (item.id === action.payload.id) {
          const newItem = Object.assign({ ...item }, action.payload);
          newItem.metaData.dataChangedTs = new Date().getTime();

          // TODO - might be deprecated, remove before merge

          // newItem.id = action.payload.id;
          // newItem.serviceMockId = action.payload.serviceMockId;
          // newItem.metaData = { ...item.metaData, trackingStatus: _.get(action.payload, 'metaData.trackingStatus') };
          // newItem.metadata = { ...item.metadata, stages: _.get(action.payload, 'metadata.stages') };
          // newItem.httpEndpoint =
          //   action.payload.httpEndpoint !== undefined ? action.payload.httpEndpoint : item.httpEndpoint;
          // newItem.httpsEndpoint =
          //   action.payload.httpsEndpoint !== undefined ? action.payload.httpsEndpoint : item.httpsEndpoint;
          // newItem.svEndpoint = item.svEndpoint;
          // newItem.status = item.status;
          // newItem.isDraft = item.isDraft;

          // const original = Object.assign({}, action.payload);
          // original.id = action.payload.serviceMockId !== undefined ? action.payload.serviceMockId : action.payload.id;
          // original.serviceMockId = action.payload.serviceMockId ? action.payload.serviceMockId : undefined;

          // We use current version of the item and put it to the original
          // newItem.original = {
          //   ...item,
          //   serviceMockId: action.payload.serviceMockId !== undefined ? action.payload.serviceMockId : -1,
          // };
          return newItem;
        }
        return item;
      });
      return msChangedState;

    case actionTypes.MOCK_SERVICE_TEMPLATE_CHANGED:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          if (item.id === action.payload.serviceMockId) {
            const newItem = Object.assign({}, item);
            const template = action.payload.newTemplate;
            newItem.templateId = template.id;
            newItem.mockServiceTransactions = template.mockServiceTransactions;
            newItem.description = template.description;
            newItem.liveSystemHost = template.liveSystemHost;
            newItem.liveSystemPort = template.liveSystemPort;
            newItem.thinkTime = template.thinkTime;
            newItem.noMatchingRequestPreference = template.noMatchingRequestPreference;

            // Override replicas, if needed
            if (action.payload.overrideReplicas === true) {
              newItem.replicas = template.replicas;
            }

            newItem.metaData.isDirty = isDirty(newItem);
            newItem.metaData.dataChangedTs = new Date().getTime();
            return newItem;
          }

          return item;
        }),
      };

    case actionTypes.MOCK_SERVICE_TRANSACTIONS_CHANGED:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          if (item.id === action.payload.serviceMockId) {
            item = {
              ...item,
              mockServiceTransactions: action.payload.mockServiceTransactions,
            };
            // TODO - check item might not be dirty (it is already assigned in the backend)
            item.metaData.isDirty = true;
            item.metaData.dataChangedTs = new Date().getTime();
          }

          return item;
        }),
      };
    case actionTypes.MOCK_SERVICE_TRANSACTION_CONTENT_CHANGED:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          if (item.id === action.payload.serviceMockId) {
            item = {
              ...item,
              mockServiceTransactions: _.map(item.mockServiceTransactions, txn => {
                if (txn.txnId === action.payload.updatedTransaction.id) {
                  txn = {
                    ...txn,
                    priority: action.payload.updatedTransaction.priority,
                  };
                }
                return txn;
              }),
            };
            item.metaData.isDirty = isDirty(item);
          }

          return item;
        }),
      };
    case actionTypes.TOGGLE_IN_PROGRESS:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          if (item.id === action.serviceMockId) {
            item = {
              ...item,
              metaData: {
                ...item.metaData,
                inProgress: !item.metaData.inProgress,
                inProgressAction: action.metaData?.inProgressAction || undefined,
              },
            };
          }
          return item;
        }),
      };
    case actionTypes.SET_IN_PROGRESS:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          if (item.id === action.serviceMockId) {
            item = {
              ...item,
              metaData: {
                ...item.metaData,
                inProgress: action.inProgress,
                inProgressAction: action.metaData?.inProgressAction || undefined,
              },
            };
          }
          return item;
        }),
      };
    case actionTypes.BULK_TOGGLE_IN_PROGRESS:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          if (action.serviceMockIds.includes(item.id)) {
            item = {
              ...item,
              metaData: {
                ...item.metaData,
                inProgress: !item.metaData.inProgress,
                inProgressAction: action.actionType === 'START' ? 'DEPLOY' : undefined,
              },
            };
          }
          return item;
        }),
      };

    case actionTypes.BULK_STAGE_UPDATE:
      return {
        ...state,
        dataList: _.map(state.dataList, item => {
          const index = _.findIndex(action.payload, stageUpdate => stageUpdate.id === item.id);
          if (index !== -1) {
            //Update stage with payload coming from tracking
            item.metaData.trackingStatus = action.payload[index].stage;

            //Check if timestamp of this stage exists (used for resuming the deploy animation)
            if (action.payload[index].stage === 'STARTING') {
              item.metadata.stages['STARTING'] = new Date().toISOString();
            }
          }
          return item;
        }),
      };
    case actionTypes.MOCK_SERVICE_TOGGLE_EXPAND_ROW:
      const stateCopy = { ...state };
      if (stateCopy.expandedMockServiceId === null || stateCopy.expandedMockServiceId !== action.payload) {
        stateCopy.expandedMockServiceId = action.payload;
      } else {
        stateCopy.expandedMockServiceId = null;
      }

      // Always open first tab
      stateCopy.activeTab = 'main';
      return stateCopy;

    case actionTypes.MOCK_SERVICE_EXPAND_ITEM: {
      return { ...state, expandedMockServiceId: action.payload };
    }
    case actionTypes.MOCK_SERVICE_COLLAPSE_ALL:
      return { ...state, expandedMockServiceId: null };

    case actionTypes.UPDATE_IS_MS_IN_WORKSPACE:
      return { ...state, isMsInWorkspace: action.payload };

    case actionTypes.STOP_MOCK_SERVICE:
    case actionTypes.DEPLOY_MOCK_SERVICE:
    case actionTypes.SAVE_OR_UPDATE_MOCK_SERVICE:
      const serviceMockIds = _.map(state.dataList, item => {
        return item.id;
      });

      let idToUse = -1;
      const tempIdFound = _.includes(serviceMockIds, action.payload.tempId);
      if (tempIdFound) {
        idToUse = action.payload.tempId;
      } else {
        idToUse = action.payload.newMockService.id;
      }

      return {
        ...state,
        dataList: _.map(state.dataList, (item: any) => {
          if (item.id === idToUse) {
            item = {
              ...action.payload.newMockService,
              hits: action.payload.newMockService.hits !== null ? action.payload.newMockService.hits : '-',
              metaData: {
                ...item.metaData,
                isDeployed:
                  action.payload.newMockService.status != null &&
                  action.payload.newMockService.status.toUpperCase() === 'RUNNING',
                isFailed:
                  action.payload.newMockService.status != null &&
                  action.payload.newMockService.status.toUpperCase() === 'FAILED',
                inProgress:
                  action.payload.newMockService.status != null &&
                  action.payload.newMockService.status.toUpperCase() === 'CONFIGURING',
                isStopped: isMSStopped(action.payload.newMockService),
                isNewest: false,
                isDirty: false,
                isFileAssetDirty: false,
              },
              original: { ...action.payload.newMockService },
            };
          }

          return item;
        }),
      };
    case actionTypes.GET_LOGS:
      if (action.payload.uniqueWindowId === state.serviceLogs.openWindowId) {
        return {
          ...state,
          serviceLogs: {
            ...state.serviceLogs,
            ...action.payload,
            isLoading: false,
          },
        };
      } else {
        return {
          ...state,
        };
      }
    case actionTypes.TOGGLE_LOADING_LOGS:
      if (action.payload === state.serviceLogs.openWindowId) {
        return {
          ...state,
          serviceLogs: {
            ...state.serviceLogs,
            isLoading: !state.serviceLogs.isLoading,
          },
        };
      } else {
        return {
          ...state,
        };
      }

    case actionTypes.MOCK_SERVICE_DETAIL_TAB_CHANGED:
      return {
        ...state,
        activeTab: action.payload,
      };

    case actionTypes.MOCK_SERVICE_PARAM_TAB_CHANGED: {
      return { ...state, activeParamTab: action.payload };
    }

    case actionTypes.SET_OPEN_WINDOW_ID:
      return {
        ...state,
        serviceLogs: {
          ...state.serviceLogs,
          isLoading: false,
          openWindowId: action.payload,
        },
      };
    case actionTypes.CREATE_MOCK_SERVICE:
      const newMockService = {
        ...action.payload.newMockService,
        metaData: {
          isDeployed: false,
          inProgress: false,
          isFailed: false,
          isNewest: true,
          isDirty: false,
          isFileAssetDirty: false,
          isInvalid: false,
          isStopped: false,
        },
        original: {
          id: action.payload.newMockService.id,
          name: action.payload.newMockService.name,
          description: action.payload.newMockService.description,
          serviceId: action.payload.newMockService.serviceId,
          harborId: action.payload.newMockService.harborId,
          shipId: action.payload.newMockService.shipId,
          endpointPreference: action.payload.newMockService.endpointPreference,
          mockServiceTransactions: action.payload.newMockService.mockServiceTransactions,
          thinkTime: action.payload.newMockService.thinkTime,
          liveSystemPort: action.payload.newMockService.liveSystemPort,
          liveSystemHost: action.payload.newMockService.liveSystemHost,
          noMatchingRequestPreference: action.payload.newMockService.noMatchingRequestPreference,
          groupTag: action.payload.newMockService.groupTag,
          capacity: action.payload.newMockService.capacity,
          assetId: action.payload.newMockService.assetId,
          serviceMockId: action.payload.newMockService.serviceMockId,
          bridgeId: action.payload.newMockService.bridgeId,
          vseId: action.payload.newMockService.vseId,
          registryId: action.payload.newMockService.registryId,
          replicas: action.payload.newMockService.replicas,
        },
      };

      let newList = _.map(state.dataList, item => {
        item = {
          ...item,
          metaData: {
            ...item.metaData,
            isNewest: false,
          },
        };

        return item;
      });

      if (action.payload.showNewItem) {
        newList = [newMockService, ...newList];
      }
      return {
        ...state,
        dataList: newList,
        isMsInWorkspace: true,
      };
    case actionTypes.FILTER_MOCK_SERVICES:
      return {
        ...state,
        filterText: action.payload,
      };
    case actionTypes.GET_LOC:
      return {
        ...state,
        locData: action.payload,
      };

    case actionTypes.OPEN_UPGRADE_MOCK_SERVICE_MODAL:
      return {
        ...state,
        isUpgradeMockModalOpen: true,
      };
    case actionTypes.CLOSE_UPGRADE_MOCK_SERVICE_MODAL:
      return {
        ...state,
        isUpgradeMockModalOpen: false,
      };

    case actionTypes.SHOW_UPGRADE_DROPDOWN_LOADING:
      return {
        ...state,
        showUpgradeLoading: true,
      };
    case actionTypes.HIDE_UPGRADE_DROPDOWN_LOADING:
      return {
        ...state,
        showUpgradeLoading: false,
      };

    default:
      return state;
  }
};
